// Questionnaire validation for the OPG CoPilot (CR-20260710-001).
//
// PRESENCE + SANITY gating ONLY — the host never judges trade quality; a structurally
// valid but poor questionnaire proceeds and the skill is the only judge (courier, §6).
// Pure functions (no Angular, no DOM) so they are unit-tested directly and reused to
// gate the RUN control.
//
// ES5 note: arrays + indexOf only.

import {
    DualSideTabInputs,
    isDualSideInputs,
    isFollowThroughOnly,
    OPG_RELATIVE_RISK_LEVELS,
    OpgAtmRangeValue,
    OpgDateSelection,
    OpgMaxRisk,
    OpgPriceRange,
    OpgSide,
    SideInputs,
    SingleSideTabInputs,
    TabInputs,
    TabKind
} from './opg-copilot-domain';
import {expirationDayKey} from './expiration-dates';

// ---- local helpers (deliberately self-contained: keep pure-logic specs dependency-free) ----

function isFiniteNumber(n: number): boolean {
    return typeof n === 'number' && isFinite(n);
}

function isValidDate(d: Date): boolean {
    return d instanceof Date && !isNaN(d.getTime());
}

// ---- Price range (§5 — four modes; atmOffset and expectedRange are OPG-only) ----

// 'strikes': both strikes must exist in the picker's strike list; DIRECTION is side-dependent
// (platform convention: Calls ascend low->high, Puts descend high->low — same convention the
// grid's own strategy-leg anchoring follows) — Calls need start < end, Puts need start > end.
// Equal strikes are invalid on either side. The list = the NEAREST expiration's strikes — the
// same list the ets-strikes-dropdown offers, so picker and validator agree by construction, A3.
// 'followThrough' (CR-20260727-002): a START STRIKE and nothing else — the direction is the
// SIDE (calls up, puts down), so there is nothing else to answer. This clause used to also
// require `direction === 'up' | 'down'`; with the selector gone that would gate the Run on a
// value the user can no longer supply. 'atmOffset' (WI-4): a valid anchor + a FINITE
// SIGNED offset (the picker only offers step-multiples, so finiteness is the sanity check —
// $0 is valid, it starts exactly at the anchor) + a sub-choice; the bounded subs (Range, Both)
// need a valid range VALUE — a positive dollar extent, or nothing when the value is the
// expected range (dynamic: the per-date end is the skill's own calculation) — a Follow-Through
// sub needs nothing.
// 'expectedRange' (CR-20260715-001): a START STRIKE and nothing else — the followThrough
// precedent for start-only modes (finiteness; the picker constrains membership). No
// direction (the side implies it), no end (the skill calculates it).
export function isOpgPriceRangeValid(pr: OpgPriceRange, strikeLadder: number[], side: OpgSide): boolean {
    if (pr == null) {
        return false;
    }
    const ladder = strikeLadder || [];
    switch (pr.kind) {
        case 'strikes':
            return isFiniteNumber(pr.startStrike)
                && isFiniteNumber(pr.endStrike)
                && (side === 'puts' ? pr.startStrike > pr.endStrike : pr.startStrike < pr.endStrike)
                && ladder.indexOf(pr.startStrike) >= 0
                && ladder.indexOf(pr.endStrike) >= 0;
        case 'followThrough':
            return isFiniteNumber(pr.startStrike);
        case 'expectedRange':
            return isFiniteNumber(pr.startStrike);
        case 'atmOffset':
            if (!(pr.anchor === 'chosen' || pr.anchor === 'market') || !isFiniteNumber(pr.offset) || pr.sub == null) {
                return false;
            }
            if (pr.sub.kind === 'range' || pr.sub.kind === 'both') {
                return isAtmRangeValueValid(pr.sub.value);
            }
            return pr.sub.kind === 'followThrough';
        default:
            return false;
    }
}

// The bounded subs' range value: static 'dollar' needs a positive extent (the step-multiple
// constraint is the picker's job, snapToStrikeStep); dynamic 'expectedRange' needs nothing —
// the checkbox alone is the whole answer (the skill calculates each date's end).
function isAtmRangeValueValid(v: OpgAtmRangeValue): boolean {
    if (v == null) {
        return false;
    }
    if (v.mode === 'dollar') {
        return isFiniteNumber(v.extent) && v.extent > 0;
    }
    return v.mode === 'expectedRange';
}

// ---- ATM Offset step snapping (WI-4 smoke revision, owner 2026-07-13) ----

// The offset + range fields are NUMERIC inputs (not dropdowns): the user types or spins, but
// every value is CONSTRAINED to a multiple of the underlying's strike step (owner: "enforce
// manual input as well, otherwise too much clicking is possible"). Snap to the NEAREST
// multiple; `minPositive` (the range extent) floors an under-a-step result at one step so a
// range is never zero. A null / non-finite value (an empty box) passes through unchanged —
// the Run simply stays gated until the user enters something.
export function snapToStrikeStep(value: number | null, step: number, minPositive: boolean): number | null {
    if (value == null || !isFiniteNumber(value) || !(step > 0)) {
        return value;
    }
    let snapped = Math.round(value / step) * step;
    if (minPositive && snapped < step) {
        snapped = step;
    }
    return snapped;
}

// ---- Min profit (§5 — strictly positive; 0 is not a profit target) ----

export function isOpgMinProfitValid(mp: { amount: number; normalized: boolean }): boolean {
    return mp != null && isFiniteNumber(mp.amount) && mp.amount > 0;
}

// ---- Max risk (§5 — FOUR relative levels; 'All' is OPG-only, never 'Auto'/'High') ----

export function isOpgMaxRiskValid(mr: OpgMaxRisk): boolean {
    if (mr == null) {
        return false;
    }
    switch (mr.kind) {
        case 'dollar':
            return isFiniteNumber(mr.amount) && mr.amount > 0;
        case 'relative':
            return OPG_RELATIVE_RISK_LEVELS.indexOf(mr.level) >= 0;
        default:
            return false;
    }
}

// ---- Date selection ----

// `loadedDate` = the panel's currently-loaded expiration resolved to a Date (threaded in by
// the orchestrator, which owns the facade — this pure validator has no facade). A 'loaded'
// selection is valid IFF that date is a real date RIGHT NOW; callers that omit it (the mock's
// date resolution — the wire never carries 'loaded') get the safe `false`.
export function isOpgDateSelectionValid(d: OpgDateSelection, loadedDate?: Date | null): boolean {
    if (d == null) {
        return false;
    }
    if (d.kind === 'single') {
        return isValidDate(d.date);
    }
    if (d.kind === 'range') {
        return isValidDate(d.start) && isValidDate(d.end) && d.start.getTime() < d.end.getTime();
    }
    if (d.kind === 'loaded') {
        return isValidDate(loadedDate);
    }
    return false;
}

// ---- Per-side / per-tab completeness (drives the ONE Run button per tab, §5) ----

export function isPresetAnchorValid(anchor: number | null | undefined): boolean {
    return isFiniteNumber(anchor) && anchor > 0 && anchor % 10 === 0;
}

// The Max Risk term is coverage-dependent (hide-risk-for-follow-through, meeting 2026-07-15
// pkg-03): a follow-through-only side has NO risk question, so it completes with maxRisk
// == null — and STRICTLY null. Strict is fail-closed: buildSkillRequest ships SideInputs
// VERBATIM (courier §6, no stripping), so a stray risk answer on a follow-through side would
// reach the wire; blocking the Run here is the guard that the forbidden state never submits.
// The state is unreachable through the UI (writeMaxRisk keeps the invariant) — this is the
// regression tripwire.
export function isSideComplete(side: SideInputs, strikeLadder: number[], opgSide: OpgSide): boolean {
    if (side && side.mode && side.mode !== 'custom' && side.mode !== 'preset') { return false; }
    if (side && side.mode === 'preset') {
        return !!side.presetId && side.presetValid === true && isPresetAnchorValid(side.presetAnchor);
    }
    return side != null
        && isOpgPriceRangeValid(side.priceRange, strikeLadder, opgSide)
        && isOpgMinProfitValid(side.minProfit)
        && (isFollowThroughOnly(side.priceRange) ? side.maxRisk == null : isOpgMaxRiskValid(side.maxRisk));
}

// Calls/Puts: date + that side. Calls & Puts: the SHARED date + BOTH sides — a collapsed
// side is visual only and still gates the Run (§5).
export function isTabComplete(kind: TabKind, inputs: TabInputs, strikeLadder: number[], loadedDate?: Date | null): boolean {
    if (inputs == null) {
        return false;
    }
    if (kind === 'callsAndPuts') {
        if (!isDualSideInputs(inputs)) {
            return false;
        }
        const dual = inputs as DualSideTabInputs;
        return isOpgDateSelectionValid(dual.date, loadedDate)
            && isSideComplete(dual.calls, strikeLadder, 'calls')
            && isSideComplete(dual.puts, strikeLadder, 'puts');
    }
    if (isDualSideInputs(inputs)) {
        return false;
    }
    const single = inputs as SingleSideTabInputs;
    return isOpgDateSelectionValid(single.date, loadedDate) && isSideComplete(single.side, strikeLadder, kind);
}

// ---- Date -> tradeable expirations (§5; the shared DATE rule lives in copilot-shared) ----

// Resolve a date selection against the panel's available (visible) expiration keys
// ('yyyy-MM-dd'). Single: that exact expiration or nothing. Range: every listed
// expiration inside [start..end] inclusive. Returns [] when nothing matches — a
// legitimately-empty resolution, not an error (§6).
export function resolveDateSelectionToExpirations(sel: OpgDateSelection, availableExpirationKeys: string[]): string[] {
    if (!isOpgDateSelectionValid(sel)) {
        return [];
    }
    const available = availableExpirationKeys || [];
    if (sel.kind === 'single') {
        const key = expirationDayKey(sel.date);
        return available.indexOf(key) >= 0 ? [key] : [];
    }
    if (sel.kind !== 'range') {
        // 'loaded' (WI-3) never reaches the wire — buildSkillRequest resolves it to single first;
        // a stray one resolves to nothing (the isOpgDateSelectionValid guard above already caught
        // a dateless 'loaded', but this narrows sel to 'range' for the start/end access below).
        return [];
    }
    // 'yyyy-MM-dd' keys compare correctly as strings.
    const startKey = expirationDayKey(sel.start);
    const endKey = expirationDayKey(sel.end);
    return available.filter(k => k >= startKey && k <= endKey);
}
